<script setup lang="ts">
import { AppTypography } from '@/components';
import ThemeToggle from '@/components/ThemeToggle.vue';
import NavIconTitle from '@/components/nav/NavIconTitle.vue';

const config = useRuntimeConfig();

const year = ref(new Date().getFullYear());

onMounted(() => {
  year.value = new Date().getFullYear();
});

const footerLinks = [
  {
    title: 'Home',
    link: '/',
  },
  {
    title: 'Marketplace',
    link: '/marketplace',
  },
  // Marketing/legal pages served by the LP on www (domain split); absolute URLs — NuxtLink treats them as external
  {
    title: 'About Us',
    link: 'https://www.tradel.com.au/about-us',
  },
  {
    title: 'Contact Us',
    link: 'https://www.tradel.com.au/contact',
  },
];

const footerLinks2 = [
  {
    title: 'Merchant Terms & Conditions',
    link: 'https://www.tradel.com.au/merchant-terms-and-conditions',
  },
  {
    title: 'Customer Terms & Conditions',
    link: 'https://www.tradel.com.au/customer-terms-and-conditions',
  },
  {
    title: 'Refunds Policy',
    link: '/refunds-policy',
  },
  {
    title: 'Privacy Policy',
    link: '/privacy-policy',
  },
];
</script>

<template>
  <div class="landing-footer-wrapper">
    <div class="landing-footer">
      <v-container fluid class="browse-max-screen-size">
        <div class="footer-container d-flex">
          <div class="footer-branding d-flex flex-column">
            <NavIconTitle :is-link="false" />
            <AppTypography class="section-description font-weight-bold">Trade delivery done right.</AppTypography>
            <AppTypography class="section-description text--secondary">
              Tradel is an Australian-owned, on-demand delivery platform built for trade, construction, and retail.
            </AppTypography>
          </div>

          <div class="footer-links d-flex">
            <div class="d-flex flex-column link-column">
              <NuxtLink v-for="link in footerLinks" :key="link.title" :to="link.link" class="text-decoration-none">
                <div class="section-description text--secondary cursor-pointer">{{ link.title }}</div>
              </NuxtLink>
            </div>
            <div class="d-flex flex-column link-column">
              <NuxtLink v-for="link in footerLinks2" :key="link.title" :to="link.link" class="text-decoration-none">
                <div class="section-description text--secondary cursor-pointer">{{ link.title }}</div>
              </NuxtLink>
            </div>
          </div>

          <div class="footer-social d-flex flex-column ga-4">
            <div class="footer-theme">
              <AppTypography class="footer-theme__label text--secondary">
                Theme
              </AppTypography>
              <div class="footer-theme__toggle">
                <ThemeToggle />
              </div>
            </div>

            <div class="d-flex ga-3">
              <div class="social-icon" :class="{ 'no-link': !config.public.facebookProfile }">
                <NuxtLink :to="config.public.facebookProfile" target="_blank">
                  <v-icon size="24" icon="mdi-facebook" />
                </NuxtLink>
              </div>
              <div class="social-icon" :class="{ 'no-link': !config.public.instagramProfile }">
                <NuxtLink :to="config.public.instagramProfile" target="_blank">
                  <v-icon size="24" icon="mdi-instagram" />
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <div class="d-flex justify-center bg-primary py-4">
      <v-container fluid class="browse-max-screen-size d-flex justify-center">
        <AppTypography class="font-weight-bold all-rights-reserved">
          {{ year }} Tradel. All rights reserved.
        </AppTypography>
      </v-container>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.landing-footer-wrapper {
  background-color: rgb(var(--v-theme-neutral));
  border-top: 1px solid rgb(var(--v-theme-stroke));
}

.landing-footer {
  padding-top: 72px;
  padding-bottom: 72px;
  padding-inline: 16px;

  @media (max-width: 768px) {
    padding-top: 40px;
    padding-bottom: 40px;

    .section-description {
      margin-bottom: 0px !important;
    }
  }

  .footer-container {
    justify-content: space-between;
    gap: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 48px;
    }
  }

  .footer-branding {
    gap: 14px;
    max-width: 450px;

    @media (max-width: 768px) {
      max-width: 100%;
      align-items: center;
    }
  }

  .footer-links {
    gap: 64px;

    .no-link {
      pointer-events: none;
    }

    @media (max-width: 768px) {
      gap: 32px 64px;
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  .footer-social {
    @media (max-width: 768px) {
      align-items: center;
    }

    .social-icon {
      padding: 12px;
      border-radius: 12px;
      background-color: rgba(var(--v-theme-primary), 0.12);
      border: 1px solid rgba(var(--v-theme-primary), 0.25);
      transition: background-color 0.2s ease;
      color: rgb(var(--v-theme--primary));

      a {
        color: inherit;
      }

      &:hover {
        background-color: rgba(var(--v-theme-primary), 0.2);
      }
    }
  }

  .footer-theme {
    display: inline-flex;
    align-items: center;
    gap: 12px;

    @media (max-width: 768px) {
      justify-content: center;
    }
  }

  .footer-theme__label {
    margin-bottom: 0 !important;
    font-weight: 500;
  }

  .footer-theme__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    min-height: 48px;
    border-radius: 12px;
    background-color: rgba(var(--v-theme-primary), 0.12);
    border: 1px solid rgba(var(--v-theme-primary), 0.25);

    :deep(.v-btn) {
      color: rgb(var(--v-theme-primary));
    }
  }

  .link-column {
    gap: 16px;

    @media (max-width: 768px) {
      gap: 24px;
    }
  }
}

.all-rights-reserved {
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 20px;
  }
}
</style>
